import React from "react";
import {
  BarChart3,
  Filter as FunnelIcon,
  Gauge,
  LineChart as LineChartIcon,
  PieChart as PieChartIcon,
  Sigma,
  TrendingUp,
} from "lucide-react";
import {
  ChartType,
  PieMode,
  SortDirection,
  TimeframePreset,
  XInterval,
  YAgg,
} from "@/types/breeze/ChartConfiguration";

export interface ChartTypeOption {
  value: ChartType;
  label: string;
  icon: React.ReactNode;
}

export const CHART_TYPES: ChartTypeOption[] = [
  { value: "column", label: "Bar Chart", icon: <BarChart3 className="w-4 h-4" /> },
  { value: "line", label: "Line Chart", icon: <LineChartIcon className="w-4 h-4" /> },
  { value: "pie", label: "Pie Chart", icon: <PieChartIcon className="w-4 h-4" /> },
  { value: "area", label: "Area Chart", icon: <TrendingUp className="w-4 h-4" /> },
  { value: "stat", label: "Stat", icon: <Sigma className="w-4 h-4" /> },
  { value: "gauge", label: "Gauge", icon: <Gauge className="w-4 h-4" /> },
  { value: "funnel", label: "Funnel", icon: <FunnelIcon className="w-4 h-4" /> },
];

export const CHART_TYPE_LABEL: Record<ChartType, string> = Object.fromEntries(
  CHART_TYPES.map((c) => [c.value, c.label]),
) as Record<ChartType, string>;

export const CHART_TYPES_WITH_X_AXIS = new Set<ChartType>([
  "column", "line", "area",
]);

export function chartNeedsXAxis(chartType: ChartType): boolean {
  return CHART_TYPES_WITH_X_AXIS.has(chartType);
}

export const AGG_OPTIONS: { value: YAgg; label: string }[] = [
  { value: "count", label: "Count" },
  { value: "sum", label: "Sum" },
  { value: "avg", label: "Average" },
  { value: "min", label: "Min" },
  { value: "max", label: "Max" },
];

export const INTERVAL_OPTIONS: { value: XInterval; label: string }[] = [
  { value: "hour", label: "Hour" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
];

export const TIMEFRAME_OPTIONS: { value: TimeframePreset; label: string }[] = [
  { value: "all", label: "All time" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "365d", label: "Last 365 days" },
  { value: "custom", label: "Custom range" },
];

export const TIMEFRAME_LABEL: Record<TimeframePreset, string> = TIMEFRAME_OPTIONS.reduce(
  (acc, o) => { acc[o.value] = o.label; return acc; },
  {} as Record<TimeframePreset, string>,
);

export const LIMIT_OPTIONS: { value: string; label: string }[] = [
  { value: "50", label: "50" },
  { value: "100", label: "100" },
  { value: "250", label: "250" },
  { value: "500", label: "500" },
  { value: "1000", label: "1,000" },
  { value: "5000", label: "5,000" },
  { value: "10000", label: "10,000" },
  { value: "25000", label: "25,000" },
  { value: "50000", label: "50,000" },
  { value: "100000", label: "100,000" },
];

export const SORT_OPTIONS: { value: SortDirection; label: string }[] = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
];

export const PIE_MODE_OPTIONS: { value: PieMode; label: string }[] = [
  { value: "count", label: "Count" },
  { value: "sum", label: "Sum" },
];

export const PLATFORM_PERIOD_OPTIONS: { value: string; label: string }[] = [
  { value: "day", label: "Last 24 hours" },
  { value: "week", label: "Last 7 days" },
  { value: "month", label: "Last 30 days" },
  { value: "quarter", label: "Last 90 days" },
  { value: "year", label: "Last year" },
];

export const QUOTA_METRIC_OPTIONS: { value: string; label: string }[] = [
  { value: "storage", label: "Database storage" },
  { value: "files", label: "File storage" },
  { value: "users", label: "Monthly active users" },
  { value: "workflows", label: "Workflow runs" },
  { value: "emails", label: "Emails sent" },
];

export const TEMPORAL_FIELD_TYPES = new Set([
  "date", "timestamp", "timestamptz",
]);

export const NUMERIC_FIELD_TYPES = new Set([
  "integer", "bigint", "decimal", "numeric", "double", "float",
]);

export const X_AXIS_FIELD_TYPES = new Set([
  "varchar", "text", "date", "timestamp", "timestamptz",
  "integer", "bigint", "boolean",
]);

export function isTemporalField(dbType: string): boolean {
  return TEMPORAL_FIELD_TYPES.has(dbType.toLowerCase());
}

export function isNumericField(dbType: string): boolean {
  return NUMERIC_FIELD_TYPES.has(dbType.toLowerCase());
}

export function isXAxisField(dbType: string): boolean {
  return X_AXIS_FIELD_TYPES.has(dbType.toLowerCase());
}
